// monitoring-ui/services/MonitoringService.ts
// Uses your existing axiosInstance — same pattern as SiteDetailView.tsx

import axiosInstance from '../Core/RestHelper';
import type {
  DeploymentSummary,
  DeploymentDetail,
  SnapshotPoint,
  AlertEvent,
  CostSummary,
  DeploymentCostRow,
  RightsizingRec,
} from '../Model/Monitoring';

export const MonitoringService = {

  // ── Deployments ───────────────────────────────────────────────────────────

  getDeployments: async (): Promise<DeploymentSummary[]> => {
    const res = await axiosInstance.get('monitor/deployments');
    return res.data;
  },

  getDeploymentStatus: async (deploymentId: string): Promise<DeploymentDetail> => {
    const res = await axiosInstance.get(`monitor/deployments/${deploymentId}/status`);
    return res.data;
  },

  getSnapshots: async (
    deploymentId: string,
    hours = 2,
    limit = 120,
  ): Promise<SnapshotPoint[]> => {
    const res = await axiosInstance.get(
      `monitor/deployments/${deploymentId}/snapshots`,
      { params: { hours, limit } },
    );
    return res.data;
  },

  getAlertHistory: async (deploymentId: string, limit = 20): Promise<AlertEvent[]> => {
    const res = await axiosInstance.get(
      `monitor/deployments/${deploymentId}/alerts`,
      { params: { limit } },
    );
    return res.data;
  },

  resolveAlert: async (alertId: number): Promise<void> => {
    await axiosInstance.patch(`monitor/alerts/${alertId}/resolve`);
  },

  deactivateDeployment: async (deploymentId: string): Promise<void> => {
    await axiosInstance.delete(`monitor/deployments/${deploymentId}`);
  },

  // ── Costs ─────────────────────────────────────────────────────────────────

  getCostSummary: async (): Promise<CostSummary> => {
    const res = await axiosInstance.get('cost-estimator/summary');
    return res.data;
  },

  getDeploymentCosts: async (): Promise<DeploymentCostRow[]> => {
    const res = await axiosInstance.get('cost-estimator/deployments');
    return res.data;
  },

  getRightsizing: async (): Promise<RightsizingRec[]> => {
    const res = await axiosInstance.get('cost-estimator/rightsizing');
    return res.data;
  },

  sendRightsizingDigest: async (recipientEmail?: string): Promise<void> => {
    await axiosInstance.post('cost-estimator/rightsizing/notify', {
      recipient_email: recipientEmail ?? null,
    });
  },
};